'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import {
  MessageSquare, Eye, Cpu, Brain,
  Image as ImageIcon, Search, ChevronLeft,
  ChevronRight, Pencil, Globe, Languages,
  Server, AppWindow
} from 'lucide-react'
import { Button } from '@sarkiai/ui'
import Image from 'next/image'

const features = [
  { id: 'text', icon: MessageSquare, hasBadge: false },
  { id: 'vision', icon: Eye, hasBadge: false },
  { id: 'tool_calling', icon: Cpu, hasBadge: false },
  { id: 'structured', icon: Brain, hasBadge: false },
  { id: 'image', icon: ImageIcon, hasBadge: false },
  { id: 'search', icon: Search, hasBadge: false },
  { id: 'drawing', icon: Pencil, hasBadge: false },
  { id: 'culture', icon: Globe, hasBadge: false },
  { id: 'languages', icon: Languages, hasBadge: false },
  { id: 'mcp', icon: Server, hasBadge: false },
  { id: 'mcp_apps', icon: AppWindow, hasBadge: false },
]

const carrouselImages = [
  '/assets/images/sections/sarkiai-carrousel/1.png',
  '/assets/images/sections/sarkiai-carrousel/2.png',
  '/assets/images/sections/sarkiai-carrousel/3.png',
  '/assets/images/sections/sarkiai-carrousel/6.png',
  '/assets/images/sections/sarkiai-carrousel/7.png',
  '/assets/images/sections/sarkiai-carrousel/8.png',
  '/assets/images/sections/sarkiai-carrousel/10.png',
]

export function SarkiAIShowcase() {
  const t = useTranslations('enterprise_page.sarkiai_showcase')
  const [activeImage, setActiveImage] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % carrouselImages.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  return (
    <section className="py-24 relative overflow-hidden text-foreground px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left Side: Mobile Showcase */}
          <div className="lg:col-span-4 order-2 lg:order-1 flex justify-center">
            <div className="relative w-[17.5rem]">
              {/* Phone Frame - Thin & Stylish */}
              <div className="relative z-10 bg-background rounded-[2rem] border-2 border-black/10 dark:border-white/10 shadow-2xl overflow-hidden aspect-[9/19]">
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-white dark:bg-white rounded-full z-20" />

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeImage}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={carrouselImages[activeImage]}
                      alt={`SarkiAI Screen ${activeImage + 1}`}
                      fill
                      className="object-cover"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation buttons for carrousel */}
              <div className="flex justify-center items-center gap-3 mt-6">
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 rounded-full border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                  onClick={() => setActiveImage((prev) => (prev - 1 + carrouselImages.length) % carrouselImages.length)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1.5">
                  {carrouselImages.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 transition-all duration-300 rounded-full ${activeImage === i ? 'w-5 bg-primary' : 'w-1.5 bg-foreground/20'}`}
                    />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 rounded-full border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                  onClick={() => setActiveImage((prev) => (prev + 1) % carrouselImages.length)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Right Side: Functionalities Grid */}
          <div className="lg:col-span-8 order-1 lg:order-2 space-y-10">
            <div className="space-y-4">
              <motion.h2
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-4xl uppercase tracking-tighter leading-none"
              >
                {t('sectionTitle')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-muted-foreground max-w-xl leading-relaxed"
              >
                {t('sectionSubtitle')}
              </motion.p>
            </div>

            {/* Grid with dividers - Unified 6-column Base */}
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-px border border-black/10 dark:border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-black/5">
              {features.map((f, i) => {
                const isMcp = f.id === 'mcp' || f.id === 'mcp_apps'
                const isLastIndividual = f.id === 'languages'

                return (
                  <motion.div
                    key={f.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className={`
                      bg-background p-6 space-y-4 group hover:bg-black/5 dark:hover:bg-white/5 transition-colors
                      ${isMcp ? 'lg:col-span-3 md:col-span-2' : (isLastIndividual ? 'lg:col-span-2 md:col-span-4' : 'lg:col-span-2 md:col-span-2')}
                    `}
                  >
                    <div className="flex justify-between items-start">
                      <div className="w-10 h-10 rounded-xl border border-black/10 dark:border-white/10 flex items-center justify-center group-hover:border-black/20 dark:group-hover:border-white/20 transition-colors">
                        <f.icon className="w-5 h-5 text-foreground" strokeWidth={1.5} />
                      </div>
                      {f.hasBadge && (
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-black/10 dark:border-white/10 text-muted-foreground whitespace-nowrap">
                          {t(`features.${f.id}.badge`)}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm uppercase tracking-tight text-foreground font-bold leading-tight">
                        {t(`features.${f.id}.title`)}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t(`features.${f.id}.desc`)}
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
